import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  IntentBadge,
  type Intent,
} from "@excellent/ui";
import type { EvidenceTaskDetailDto } from "@excellent/ui/api/contracts/evidence";

const NUM = "tabular-nums";

export function ProofStoryCard({ detail }: { detail: EvidenceTaskDetailDto }) {
  const rejectCount = detail.judgments.filter((j) => j.verdict === "reject").length;
  const approveCount = detail.judgments.filter((j) => j.verdict === "approve").length;
  const passingReceipts = detail.receipts.filter((r) => r.exitCode === 0).length;
  const failingReceipts = detail.receipts.length - passingReceipts;
  const gapCount =
    detail.residualGaps.gapCount == null
      ? "scope unknown"
      : String(detail.residualGaps.gapCount);
  const decisionCopy =
    detail.verdict === "accepted"
      ? rejectCount > 0
        ? `Accepted after ${rejectCount} reject${rejectCount === 1 ? "" : "s"}`
        : "Accepted"
      : detail.verdict === "rejected"
        ? "Rejected"
        : detail.status === "review"
          ? "Awaiting verifier"
          : "No decision";
  const trustIntent: Intent = detail.bundle.found
    ? detail.bundle.trust.status === "signed"
      ? "success"
      : "warning"
    : "danger";

  const steps = [
    {
      label: "Claim",
      value: `${detail.claims.length} asserted`,
      detail:
        detail.claims.length > 0
          ? "Bound to recorded workflow claims"
          : "No claim rows are bound yet",
      intent: detail.claims.length > 0 ? "success" : "warning",
    },
    {
      label: "Checks",
      value: `${passingReceipts}/${detail.receipts.length} passed`,
      detail:
        failingReceipts > 0
          ? `${failingReceipts} receipt${failingReceipts === 1 ? "" : "s"} failed`
          : detail.waterfall.summary,
      intent:
        detail.receipts.length === 0 ? "warning" : failingReceipts > 0 ? "danger" : "success",
    },
    {
      label: "Decision",
      value: decisionCopy,
      detail:
        approveCount > 0 || rejectCount > 0
          ? `${approveCount} approve · ${rejectCount} reject`
          : "No verifier decision recorded",
      intent:
        detail.verdict === "accepted"
          ? "success"
          : detail.verdict === "rejected"
            ? "danger"
            : "warning",
    },
    {
      label: "Evidence pack",
      value: detail.bundle.found ? detail.bundle.trust.status : "missing",
      detail: detail.bundle.found
        ? detail.bundle.trust.summary
        : detail.bundle.reason ?? "not composable",
      intent: trustIntent,
    },
    {
      label: "Residual gaps",
      value: gapCount,
      detail: detail.residualGaps.summary,
      intent:
        detail.residualGaps.gapCount === 0
          ? "success"
          : detail.residualGaps.gapCount == null
            ? "warning"
            : "danger",
    },
  ] satisfies Array<{
    label: string;
    value: string;
    detail: string;
    intent: Intent;
  }>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Proof story</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-px overflow-hidden rounded-lg border border-border bg-border lg:grid-cols-5">
          {steps.map((step, index) => (
            <div key={step.label} className="bg-card p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="type-subtext text-muted-foreground">
                  {index + 1}. {step.label}
                </span>
                <IntentBadge intent={step.intent}>{step.value}</IntentBadge>
              </div>
              <p className="mt-2 line-clamp-3 type-subtext text-muted-foreground">
                {step.detail}
              </p>
            </div>
          ))}
        </div>
        <div className="grid gap-3 type-body lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div>
            <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
              What this proves
            </div>
            <p className="mt-1 text-muted-foreground">
              {detail.consequenceTag
                ? `This run ties ${detail.claims.length} asserted claim${detail.claims.length === 1 ? "" : "s"} and ${detail.receipts.length} check receipt${detail.receipts.length === 1 ? "" : "s"} to a ${detail.consequenceTag} consequence.`
                : "This run is missing a named consequence, so the proof is not buyer-ready yet."}
            </p>
          </div>
          <div>
            <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
              Replay path
            </div>
            <p className="mt-1 text-muted-foreground">
              Use <span className="font-medium text-foreground">Prove it</span> to emit the audited pack, then offline-verify the bundle hash, claim chain, receipts, and trust root.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
